import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import { Building2, ChevronDown, Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Establishment Selector for users with access to multiple branches
export const EstablishmentSelector = ({ onSelect }) => {
  const [establishments, setEstablishments] = useState([]);
  const [currentEstablishment, setCurrentEstablishment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  
  useEffect(() => {
    fetchEstablishments();
    // Load current selection from localStorage
    const stored = localStorage.getItem('current_establishment');
    if (stored) {
      try {
        setCurrentEstablishment(JSON.parse(stored));
      } catch {
        // Ignore parse errors
      }
    }
  }, []);
  
  const fetchEstablishments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/establishments`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEstablishments(data);
        
        // If only one establishment, auto-select it
        if (data.length === 1 && !currentEstablishment) {
          handleSelect(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching establishments:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelect = (establishment) => {
    setCurrentEstablishment(establishment);
    localStorage.setItem('current_establishment', JSON.stringify(establishment));
    setOpen(false);
    
    if (onSelect) {
      onSelect(establishment);
    }
    
    toast.success(`Establecimiento: ${establishment.nombre}`);
  };
  
  // Don't render if no establishments or only one
  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }
  
  if (establishments.length <= 1) {
    // Show current establishment name if there's one
    if (establishments.length === 1) {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100">
          <Building2 className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-700">{establishments[0].nombre}</span>
        </div>
      );
    }
    return null;
  }
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="flex items-center gap-2"
          data-testid="establishment-selector"
        >
          <Building2 className="w-4 h-4" />
          <span className="max-w-[150px] truncate">
            {currentEstablishment?.nombre || 'Seleccionar Sede'}
          </span>
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Cambiar Establecimiento</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {establishments.map((est) => (
          <DropdownMenuItem 
            key={est.id}
            onClick={() => handleSelect(est)}
            className="flex items-center justify-between cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <div>
                <p className="font-medium">{est.nombre}</p>
                {est.ciudad && (
                  <p className="text-xs text-gray-500">{est.ciudad}</p>
                )}
              </div>
            </div>
            {currentEstablishment?.id === est.id && (
              <Check className="w-4 h-4 text-green-500" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};


// Hook to manage current establishment context
export const useCurrentEstablishment = () => {
  const [currentEstablishment, setCurrentEstablishment] = useState(null);
  
  useEffect(() => {
    const stored = localStorage.getItem('current_establishment');
    if (stored) {
      try {
        setCurrentEstablishment(JSON.parse(stored));
      } catch {
        // Ignore parse errors
      }
    }
  }, []);
  
  const selectEstablishment = (establishment) => {
    setCurrentEstablishment(establishment);
    localStorage.setItem('current_establishment', JSON.stringify(establishment));
  };
  
  const clearEstablishment = () => {
    setCurrentEstablishment(null);
    localStorage.removeItem('current_establishment');
  };
  
  return {
    currentEstablishment,
    selectEstablishment,
    clearEstablishment,
    establishmentId: currentEstablishment?.id
  };
};

export default EstablishmentSelector;
